import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import BaseTool, { UIRenderData } from './BaseTool';
import Engine from '../Engine';
import { RenderLayers, CustomEventTypes, ToolState, UnitToShortLabel, ToolTypes, WorldUnitScales} from '../../globals';
import Input from '../Input';
import SetDistanceToolPoints from '../../actions/tools/SetDistanceToolPoints';
import store from '../../state/store';
import Callout from '../Callout';
import WorkspaceManager from '../WorkspaceManager';
import { MouseButtons } from '../../interfaces/Input';

const SNAP_THRESHOLD = 0.1;
const tempA = new THREE.Vector3();
const tempB = new THREE.Vector3();
const tempPosition = new THREE.Vector3();
const vertex0 = new THREE.Vector3();
const vertex1 = new THREE.Vector3();
const vertex2 = new THREE.Vector3();

const axisIndex =
{
	main: 0,
	x: 1,
	y: 2,
	z: 3,
};

const handleId =
{
	none: 0,
	startPoint: 1,
	endPoint: 2,
};

export default class DistanceTool extends BaseTool
{
	toolType: ToolTypes;
	UIRenderData: UIRenderData;
	startPoint: THREE.Vector3;
	endPoint: THREE.Vector3;
	finalizedEndPoint: THREE.Vector3;
	raycaster: THREE.Raycaster;
	decimals: number;
	interactableHandleId: number;
	hoveringMovingPoint: boolean;
	lineMaterials: LineMaterial[];
	lineMeshes: Line2[];
	colors: THREE.Color[];
	worldUnitLabel: string;

	constructor()
	{
		super();

		this.updateDisplayUnit = this.updateDisplayUnit.bind(this);
		this.onWindowResize = this.onWindowResize.bind(this);
		this.startPoint = new THREE.Vector3( 0, 0, 0 );
		this.endPoint = new THREE.Vector3( 0, 0, 0 );
		this.finalizedEndPoint = new THREE.Vector3( 0, 0, 0 );
		this.raycaster = new THREE.Raycaster();
		this.decimals = 2;
		this.interactableHandleId = handleId.none;
		this.hoveringMovingPoint = false;

		const resolution = new THREE.Vector2( window.innerWidth, window.innerHeight );

		this.name = "Distance Tool";
		this.toolType = ToolTypes.DistanceTool;
		this.worldUnitLabel = UnitToShortLabel[WorldUnitScales.Meters];
		const numAxes = Object.keys(axisIndex).length;
		this.lineMaterials = new Array(numAxes);
		this.lineMeshes = new Array(numAxes);
		this.colors = new Array(numAxes);
		this.colors[axisIndex.main] = new THREE.Color(0, 1, 1);
		this.colors[axisIndex.x] = new THREE.Color(1, 0, 0);
		this.colors[axisIndex.y] = new THREE.Color(0, 1, 0);
		this.colors[axisIndex.z] = new THREE.Color(0, 0, 1);
		this.UIRenderData =
		{
			labels: new Array(numAxes),
			interactiveHandles: new Array(2)
		};

		for (let i = 0; i < numAxes; i++)
		{
			this.UIRenderData.labels[i] =
			{
				visible: false,
				color: this.colors[i],
				text: "",
				position: new THREE.Vector3(),
				renderPosition: undefined,
				interactiveHandles: []
			};

			this.lineMaterials[i] = new LineMaterial({
				color: this.colors[i],
				linewidth: 3,
				resolution: resolution,
				depthTest: false,
				dashed: false

			});

			const geometry = new LineGeometry();
			geometry.setPositions([0, 0, 0, 0, 0, 0]);
			this.lineMeshes[i] = new Line2(geometry, this.lineMaterials[i]);
			this.lineMeshes[i].userData.parentTool = this;
			this.attach(this.lineMeshes[i]);
			this.setAxisVisible(i, false);
		}

		this.UIRenderData.interactiveHandles[0] =
		{
			id: handleId.startPoint,
			visible: false,
			color: this.colors[axisIndex.main],
			position: new THREE.Vector3(),
			renderPosition: undefined,
		};

		this.UIRenderData.interactiveHandles[1] =
		{
			id: handleId.endPoint,
			visible: false,
			color: this.colors[axisIndex.main],
			position: new THREE.Vector3(),
			renderPosition: undefined,
		};

		this.traverse(function(child)
		{
			child.frustumCulled = false;
			child.layers.disableAll();
			child.renderOrder = 2;
		});

		this.updateDisplayUnit();
		document.addEventListener(CustomEventTypes.onDisplayUnitChange, this.updateDisplayUnit);
		window.addEventListener('resize', this.onWindowResize);
	}

	onDispose()
	{
		document.removeEventListener(CustomEventTypes.onDisplayUnitChange, this.updateDisplayUnit);
		window.removeEventListener('resize', this.onWindowResize);
	}

	onWindowResize()
	{
		for (let i = 0; i < Object.keys(axisIndex).length; i++)
		{
			this.lineMaterials[i].uniforms.resolution.value.set( window.innerWidth, window.innerHeight);
		}
	}

	getLabelWidth()
	{
		return (window.innerWidth / 32);
	}

	getLabelHeight()
	{
		return (window.innerWidth / 45);
	}

	getInteractiveHandleSize()
	{
		return (window.innerWidth / 128);
	}
	
	setHighlightEnabled(enabled)
	{
		const factor = 0.45;
		if (enabled)
		{
			this.colors[axisIndex.main].addScalar(factor);
			this.lineMaterials[axisIndex.main].color = this.colors[axisIndex.main];
		}
		else
		{
			this.colors[axisIndex.main].addScalar(-factor);
			this.lineMaterials[axisIndex.main].color = this.colors[axisIndex.main];
		}

		//this.lineMeshes[axisIndex.main].geometry.elementsNeedUpdate = true;
		this.updateLabels();
	}

	setInteractableHandleId(id)
	{
		this.interactableHandleId = id;
	}

	get isActive() : boolean
	{
		return this._isActive;
	}

	set isActive(value : boolean)
	{	
		if (this.state === ToolState.Finalized)
		{
			this.UIRenderData.interactiveHandles[0].visible = value;
			this.UIRenderData.interactiveHandles[1].visible = value;
			this._isActive = value;
		}

		this.updateLabels();
	}

	public get state() : ToolState
	{
		return this._state;
	}

	public set state(value : ToolState)
	{
		this._state = value;
		switch (this.state)
		{
			case ToolState.InitialPlacement:
				this.endPoint.copy(this.startPoint);
				this.setAxisVisible(axisIndex.main, false);
				break;
			case ToolState.Measure:
			case ToolState.Remeasure:
				break;
			default:
				break;
		}
	}

	getUIRenderData()
	{
		return this.UIRenderData;
	}

	forceFinalize()
	{
		switch (this.state)
		{
			case ToolState.InitialPlacement:
				this.setEndPoint(this.startPoint);
				this.finalizedEndPoint.copy(this.endPoint);
				break;
			case ToolState.Measure:
			case ToolState.Remeasure:
				this.finalizedEndPoint.copy(this.endPoint);
				break;
			default:
				break;
		}
		this.state = ToolState.Finalized;
	}

	updateDisplayUnit()
	{
		this.worldUnitLabel = UnitToShortLabel[Engine.getInstance().getDisplayUnitScale()];
		if (this.state === ToolState.Finalized)
		{
			this.updateLabels();
		}
	}

	setStartingPoint(point?: THREE.Vector3)
	{
		if (!point)
		{
			this.UIRenderData.interactiveHandles[0].visible = false;
			return;
		}
		this.startPoint.copy(point);
		this.endPoint.copy(point);
		const hoveredWindowId = store.getState().workspaceModule.hoveredWindowId;
		this.UIRenderData.interactiveHandles[0].visible = (WorkspaceManager.getSceneViewData(hoveredWindowId) !== undefined);
		this.UIRenderData.interactiveHandles[0].position.x = point.x;
		this.UIRenderData.interactiveHandles[0].position.y = point.y;
		this.UIRenderData.interactiveHandles[0].position.z = point.z;
	}

	setEndPoint(point? : THREE.Vector3)
	{
		this.isActive = false;

		if (!point)
		{
			this.UIRenderData.interactiveHandles[1].visible = false;
			this.setAxisVisible(axisIndex.main, false);
			return;
		}
		this.UIRenderData.interactiveHandles[1].visible = true;
		this.setAxisVisible(axisIndex.main, true);
		this.endPoint.copy(point);
		this.UIRenderData.interactiveHandles[1].position.x = point.x;
		this.UIRenderData.interactiveHandles[1].position.y = point.y;
		this.UIRenderData.interactiveHandles[1].position.z = point.z;
		this.lineMeshes[axisIndex.main].geometry.setPositions([this.startPoint.x, this.startPoint.y, this.startPoint.z, this.endPoint.x, this.endPoint.y, this.endPoint.z]);

		this.updateLabels();
	}

	setAxisVisible(index: number, visible: boolean)
	{
		this.UIRenderData.labels[index].visible = visible;
		this.lineMeshes[index]. visible = visible;
		if (visible)
		{
			this.lineMeshes[index].layers.enable(RenderLayers.MainCameraOnly);
		}
		else
		{
			this.lineMeshes[index].layers.disable(RenderLayers.MainCameraOnly);
		}
	}

	updateLabel(index: number, length: number, position: THREE.Vector3)
	{
		length = Math.round((length + Number.EPSILON) * Math.pow(10, this.decimals)) / Math.pow(10, this.decimals);
		if (length === 0)
		{
			this.setAxisVisible(index, false);
			return;
		}

		let text = length.toString() + this.worldUnitLabel;
		if (index !== axisIndex.main)
		{
			for (const [key, value] of Object.entries(axisIndex))
			{
				if (value === index)
				{
					text = key + " : " + text;
					break;
				}
			}
		}
		this.UIRenderData.labels[index].text = text;
		this.UIRenderData.labels[index].position.x = position.x;
		this.UIRenderData.labels[index].position.y = position.y;
		this.UIRenderData.labels[index].position.z = position.z;
		this.setAxisVisible(index, true);
	}

	updateLabels()
	{
		let distance = this.startPoint.distanceTo(this.endPoint) / Engine.getInstance().getDisplayUnitScale();
		tempPosition.copy(this.endPoint).sub(this.startPoint).divideScalar(2).add(this.startPoint);
		this.updateLabel(axisIndex.main, distance, tempPosition);

		const axesVisible = this.state === ToolState.Finalized && this.isActive;
		if (axesVisible)
		{
			const deltaX = this.endPoint.x - this.startPoint.x;
			const deltaY = this.endPoint.y - this.startPoint.y;
			const deltaZ = this.endPoint.z - this.startPoint.z;

			tempA.copy(this.startPoint);
			if (Math.abs(deltaX) > Number.EPSILON)
			{
				tempA.x += deltaX;
				this.lineMeshes[axisIndex.x].geometry.setPositions([this.startPoint.x, this.startPoint.y, this.startPoint.z, tempA.x, tempA.y, tempA.z]);

				distance = this.startPoint.distanceTo(tempA) / Engine.getInstance().getDisplayUnitScale();
				tempPosition.copy(tempA).sub(this.startPoint).divideScalar(2).add(this.startPoint);
				this.updateLabel(axisIndex.x, distance, tempPosition);
			}
			tempB.copy(tempA);
			if (Math.abs(deltaY) > Number.EPSILON)
			{
				tempB.y += deltaY;
				this.lineMeshes[axisIndex.y].geometry.setPositions([tempA.x, tempA.y, tempA.z, tempB.x, tempB.y, tempB.z]);
				distance = tempA.distanceTo(tempB) / Engine.getInstance().getDisplayUnitScale();
				tempPosition.copy(tempB).sub(tempA).divideScalar(2).add(tempA);
				this.updateLabel(axisIndex.y, distance, tempPosition);
			}
			if (Math.abs(deltaZ) > Number.EPSILON)
			{
				this.lineMeshes[axisIndex.z].geometry.setPositions([tempB.x, tempB.y, tempB.z, this.endPoint.x, this.endPoint.y, this.endPoint.z]);
				distance = tempB.distanceTo(this.endPoint) / Engine.getInstance().getDisplayUnitScale();
				tempPosition.copy(this.endPoint).sub(tempB).divideScalar(2).add(tempB);
				this.updateLabel(axisIndex.z, distance, tempPosition);
			}

			//if we only ended up with 1 visible axis, hide it too (its going to be parallal to the main axis)
			let numVisible = 0;
			if (this.lineMeshes[axisIndex.x].visible) numVisible++;
			if (this.lineMeshes[axisIndex.y].visible) numVisible++;
			if (this.lineMeshes[axisIndex.z].visible) numVisible++;
			if (numVisible === 1)
			{
				this.setAxisVisible(axisIndex.x, false);
				this.setAxisVisible(axisIndex.y, false);
				this.setAxisVisible(axisIndex.z, false);
			}
		}
		else
		{
			this.setAxisVisible(axisIndex.x, false);
			this.setAxisVisible(axisIndex.y, false);
			this.setAxisVisible(axisIndex.z, false);
		}
	}

	getIntersectionPoint()
	{
		const intersections = this.raycaster.intersectObjects(Engine.getInstance().getSelectionRoot().children, true);
		for (const intersection of intersections)
		{
			if (intersection.object.userData.isModelRoot || intersection.object instanceof Callout) continue;
			const face = intersection.face;
			const mesh = intersection.object as THREE.Mesh;
			if(face)
			{
				if (mesh.geometry instanceof THREE.BufferGeometry)
				{
					const positions = mesh.geometry.attributes.position;
					vertex0.fromBufferAttribute(positions, face.a).applyMatrix4(intersection.object.matrixWorld);
					vertex1.fromBufferAttribute(positions, face.b).applyMatrix4(intersection.object.matrixWorld);
					vertex2.fromBufferAttribute(positions, face.c).applyMatrix4(intersection.object.matrixWorld);
				}
				else
				{
					const geometry = mesh.geometry as any;
					if(geometry.vertices)
					{
						vertex0.copy(geometry.vertices[face.a]).applyMatrix4(intersection.object.matrixWorld);
						vertex1.copy(geometry.vertices[face.b]).applyMatrix4(intersection.object.matrixWorld);
						vertex2.copy(geometry.vertices[face.c]).applyMatrix4(intersection.object.matrixWorld);
					}
					else
					{
						return intersection.point;
					}
				}
			}
			
			let snapThreshold = SNAP_THRESHOLD;
			const point = intersection.point;
			const closestVertex = new THREE.Vector3();
			const distance0 = vertex0.distanceTo(point);
			const distance1 = vertex1.distanceTo(point);
			const distance2 = vertex2.distanceTo(point);

			if (distance0 <= snapThreshold)	{closestVertex.copy(vertex0); snapThreshold = distance0;}
			if (distance1 <= snapThreshold)	{closestVertex.copy(vertex1); snapThreshold = distance1;}
			if (distance2 <= snapThreshold)	{closestVertex.copy(vertex2); snapThreshold = distance2;}

			if (closestVertex) return closestVertex;
			return intersection.point;
		}

		if (Engine.getInstance().getGrid().visible)
		{
			this.raycaster.layers.enable(RenderLayers.MainCameraOnly);
			intersections.length = 0;
			this.raycaster.intersectObject(Engine.getInstance().getGrid(), false, intersections);
			this.raycaster.layers.disable(RenderLayers.MainCameraOnly);
			for (const intersection of intersections)
			{
				const step = 0.01;
				const previousX = Math.floor(intersection.point.x / step) * step;
				const nextX = previousX + step;
				const previousZ = Math.floor(intersection.point.z / step) * step;
				const nextZ = previousZ + step;
				intersection.point.x = (Math.abs(intersection.point.x - previousX) <= Math.abs(intersection.point.x - nextX)) ? previousX : nextX;
				intersection.point.z = (Math.abs(intersection.point.z - previousZ) <= Math.abs(intersection.point.z - nextZ)) ? previousZ : nextZ;
				return intersection.point;
			}
		}

		return undefined;
	}

	update()
	{
		switch (this.state)
		{
			case ToolState.InitialPlacement:
				{
					const hoveredWindowId = store.getState().workspaceModule.hoveredWindowId;
					const sceneViewData = WorkspaceManager.getSceneViewData(hoveredWindowId);
					if (sceneViewData && sceneViewData.context)
					{
						this._sceneViewId = hoveredWindowId;
						this.raycaster.setFromCamera(Input.getMousePositionInRect(sceneViewData.context.canvas.getBoundingClientRect()), sceneViewData.activeCamera);
						const point = this.getIntersectionPoint();
						this.setStartingPoint(point);
						if (point && Input.getMouseButtonDown(MouseButtons.LEFT))
						{
							new SetDistanceToolPoints(this, ToolState.Measure, this.state, false, this.isActive, point);
						}
					}
				}
				break;
			case ToolState.Measure:
			case ToolState.Remeasure:
				{
					const hoveredWindowId = store.getState().workspaceModule.hoveredWindowId;
					if (WorkspaceManager.getSceneViewData(hoveredWindowId))
					{
						const camera = Engine.getInstance().getCamera();
						if(!camera) return;

						this.raycaster.setFromCamera(Input.getMousePosition(), camera);
						const point = this.getIntersectionPoint();
						this.setEndPoint(point);
						if (point && Input.getMouseButtonDown(MouseButtons.LEFT))
						{
							if (this.state === ToolState.Measure)
							{
								new SetDistanceToolPoints(this, ToolState.Finalized, this.state, true, true, this.startPoint.clone(), this.startPoint.clone(), point);
							}
							else
							{
								new SetDistanceToolPoints(this, ToolState.Finalized, ToolState.Finalized, true,	true, this.startPoint.clone(), this.startPoint.clone(), point, this.finalizedEndPoint.clone());
							}
							this.finalizedEndPoint.copy(this.endPoint);
						}
					}
					else
					{
						this.setEndPoint(undefined);
					}
				}
				break;
			case ToolState.Finalized:
				if (this.interactableHandleId !== handleId.none)
				{
					if (Input.getMouseButtonDown(MouseButtons.LEFT))
					{
						if (this.interactableHandleId === handleId.startPoint)
						{
							const tempStartPoint = this.startPoint.clone();
							this.setStartingPoint(this.endPoint);
							this.setEndPoint(tempStartPoint);
							this.finalizedEndPoint.copy(tempStartPoint);
							this.state = ToolState.Remeasure;
						}
						else if (this.interactableHandleId === handleId.endPoint)
						{
							this.state = ToolState.Remeasure;
						}
					}
				}
				break;
		}
	}
}
